// FÁZE 5 — Higher or Lower: řetězová hádací hra nad jednou statistikou.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsDownUp, CaretUp, CaretDown } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { funDuel, funResult } from "../lib/lab-api";

export default function HigherLower() {
    const { user } = useAuth();
    const [stat, setStat] = useState(null);
    const [current, setCurrent] = useState(null);
    const [nextP, setNextP] = useState(null);
    const [revealed, setRevealed] = useState(false);
    const [streak, setStreak] = useState(0);
    const [best, setBest] = useState(0);
    const [over, setOver] = useState(false);

    const start = useCallback(async () => {
        try {
            const d = await funDuel({ count: 2 });
            setStat(d.stat);
            setCurrent(d.players[0]);
            setNextP(d.players[1]);
            setStreak(0); setOver(false); setRevealed(false);
        } catch { toast.error("Načtení hry selhalo"); }
    }, []);
    useEffect(() => { start(); }, [start]);

    const advance = async () => {
        try {
            const d = await funDuel({ count: 1, stat: stat.key, exclude: nextP.fpl_id });
            setCurrent(nextP);
            setNextP(d.players[0]);
            setRevealed(false);
        } catch { toast.error("Načtení dalšího hráče selhalo"); }
    };

    const guess = async (higher) => {
        if (revealed || over || !nextP) return;
        setRevealed(true);
        const correct = higher ? Number(nextP.value) >= Number(current.value) : Number(nextP.value) <= Number(current.value);
        if (user) { try { await funResult("higher_lower", correct); } catch { /* ignore */ } }
        if (correct) {
            const s = streak + 1;
            setStreak(s); setBest((b) => Math.max(b, s));
            setTimeout(advance, 1200);
        } else {
            setOver(true);
        }
    };

    return (
        <AppShell>
            <div className="max-w-3xl mx-auto space-y-6" data-testid="higher-lower-page">
                <header className="text-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow">Fun · Higher or Lower</div>
                    <h1 className="font-display font-black uppercase text-2xl md:text-3xl mt-1 flex items-center justify-center gap-2">
                        <ArrowsDownUp size={24} className="text-fa-yellow" /> Výš, nebo níž?
                    </h1>
                    <div className="font-mono text-[10px] text-white/50 mt-2" data-testid="hilo-score">
                        Streak {streak} · rekord {best}{stat ? ` · statistika: ${stat.label}` : ""}
                    </div>
                </header>
                {current && nextP && (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="border border-white/15 bg-black/40 p-6 text-center" data-testid="hilo-current">
                            <div className="font-display font-black text-xl">{current.name}</div>
                            <div className="font-mono text-[10px] uppercase text-white/45 mt-1">{current.team} · {current.position}</div>
                            <div className="font-display font-black text-3xl mt-3 tabular-nums text-fa-cream">{current.value}</div>
                            <div className="font-mono text-[8px] uppercase text-white/35 mt-1">{stat?.label}</div>
                        </div>
                        <div className="border border-fa-yellow/40 bg-black/40 p-6 text-center" data-testid="hilo-next">
                            <div className="font-display font-black text-xl">{nextP.name}</div>
                            <div className="font-mono text-[10px] uppercase text-white/45 mt-1">{nextP.team} · {nextP.position}</div>
                            <div className={`font-display font-black text-3xl mt-3 tabular-nums ${revealed ? "text-fa-yellow" : "text-white/20"}`} data-testid="hilo-next-value">
                                {revealed ? nextP.value : "?"}
                            </div>
                            {!revealed && !over && (
                                <div className="flex justify-center gap-2 mt-3">
                                    <button type="button" onClick={() => guess(true)} data-testid="hilo-higher"
                                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-mint text-black font-bold flex items-center gap-1">
                                        <CaretUp size={12} weight="bold" /> Výš
                                    </button>
                                    <button type="button" onClick={() => guess(false)} data-testid="hilo-lower"
                                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-yellow text-black font-bold flex items-center gap-1">
                                        <CaretDown size={12} weight="bold" /> Níž
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {over && (
                    <div className="text-center space-y-3" data-testid="hilo-game-over">
                        <div className="font-display font-black uppercase text-lg text-red-300">Konec hry — streak {streak}</div>
                        <button type="button" onClick={start} data-testid="hilo-restart"
                                className="font-mono text-xs uppercase tracking-widest px-6 py-3 bg-fa-yellow text-black font-bold">
                            Hrát znovu
                        </button>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
